import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../pages/UserContext';
import { SUPERUSER_TOOLS_URL, SUPPORT_MODE_WARNING_DURATION_MS } from '../utlities/core.constants';
import AppModal from './modals/AppModal';
import { InactivityWarningContext } from '../contexts/InactivityWarningContext';

/** Formats the time left until expiry as "M:SS", or null when unknown/elapsed. */
function formatRemaining(expiresAtMs, nowMs) {
  if (!expiresAtMs) {
    return null;
  }
  const totalSeconds = Math.max(0, Math.floor((expiresAtMs - nowMs) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * Persistent, high-visibility overlay shown whenever a SuperUser is impersonating an organization.
 * It stays fixed to the bottom of the viewport on every page so the SuperUser cannot forget they are
 * acting as another org, and it provides the single Exit control that ends the session.
 */
export default function ImpersonationBanner() {
  const { isImpersonating, impersonation, realUser, endImpersonation } = useContext(UserContext);
  const { isWarningOpen: inactivityWarningOpen } = useContext(InactivityWarningContext);
  const navigate = useNavigate();
  const bannerRef = useRef(null);
  const [now, setNow] = useState(() => Date.now());
  const [warningDismissed, setWarningDismissed] = useState(false);
  const expiredHandled = useRef(false);

  const expiresAtMs = impersonation?.expiresAt ? new Date(impersonation.expiresAt).getTime() : null;

  // A new session (new expiry) resets the one-time expiry handling and any dismissed warning.
  useEffect(() => {
    expiredHandled.current = false;
    setWarningDismissed(false);
  }, [expiresAtMs]);

  // Tick once a second while impersonating so the remaining-time readout stays current and we can
  // drop the session the moment it expires.
  useEffect(() => {
    if (!isImpersonating) {
      return undefined;
    }
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [isImpersonating]);

  // When the session's TTL passes, drop the effective identity and send the SuperUser back to the
  // tool with a "session ended" notice (the server also fails writes closed once it expires).
  useEffect(() => {
    if (!isImpersonating || !expiresAtMs || expiredHandled.current) {
      return;
    }
    if (now >= expiresAtMs) {
      expiredHandled.current = true;
      Promise.resolve(endImpersonation?.()).finally(() => {
        navigate(SUPERUSER_TOOLS_URL, { state: { impersonationExpired: true } });
      });
    }
  }, [now, expiresAtMs, isImpersonating, endImpersonation, navigate]);

  // The banner is fixed to the viewport, so reserve the same height in the page and in
  // scroll-padding. Otherwise the footer and other bottom controls sit underneath it.
  useLayoutEffect(() => {
    const node = bannerRef.current;
    if (!node) {
      return undefined;
    }

    const root = document.documentElement;
    const previousPadding = document.body.style.paddingBottom;
    const previousScrollPadding = root.style.scrollPaddingBottom;
    const apply = () => {
      const height = node.offsetHeight > 0 ? `${node.offsetHeight}px` : "5.5rem";
      document.body.style.paddingBottom = height;
      root.style.scrollPaddingBottom = height;
    };
    apply();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(apply);
    observer?.observe(node);

    return () => {
      observer?.disconnect();
      document.body.style.paddingBottom = previousPadding;
      root.style.scrollPaddingBottom = previousScrollPadding;
    };
  }, [isImpersonating, impersonation]);

  if (!isImpersonating || !impersonation) {
    return null;
  }

  const onExit = async () => {
    await endImpersonation();
    navigate(SUPERUSER_TOOLS_URL);
  };

  const orgLabel = impersonation.ownerName || impersonation.ownerId;
  const actorEmail = realUser?.email;
  const remainingLabel = formatRemaining(expiresAtMs, now);

  const msRemaining = expiresAtMs ? expiresAtMs - now : null;
  // USWDS supports one modal at a time, so yield to the force-action inactivity warning; ours
  // re-evaluates on the next tick once that one is dismissed.
  const isWarningOpen =
    msRemaining != null &&
    msRemaining > 0 &&
    msRemaining <= SUPPORT_MODE_WARNING_DURATION_MS &&
    !warningDismissed &&
    !inactivityWarningOpen;

  const dismissWarning = () => setWarningDismissed(true);
  const handleWarningStateChanged = (next) => {
    if (typeof next === 'boolean' && !next) {
      dismissWarning();
    }
  };

  return (
    <>
    <div
      ref={bannerRef}
      role="region"
      aria-label="Support mode"
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 10000,
        backgroundColor: '#b50909',
        color: '#ffffff',
        borderTop: '4px solid #7a0606',
        padding: '0.75rem 1rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
        boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.35)',
      }}
    >
      <span style={{ fontWeight: 700 }}>
        {/* Announce the static support-mode context once. The per-second countdown is deliberately
            kept OUT of this live region (as an aria-hidden sibling, not a child) so assistive tech
            doesn't re-read the whole banner every second (WCAG 4.1.3); the "ending soon" modal is
            the accessible warning for the time-sensitive part. */}
        <span aria-live="polite">
          Support Mode — you are acting as <span style={{ textDecoration: 'underline' }}>{orgLabel}</span>.
          {actorEmail ? <> Signed in as {actorEmail}.</> : null}
          {' '}All changes are recorded under your account.
        </span>
        {remainingLabel ? <span aria-hidden="true"> Time remaining: {remainingLabel}.</span> : null}
      </span>
      <button
        type="button"
        onClick={onExit}
        style={{
          backgroundColor: '#ffffff',
          color: '#b50909',
          whiteSpace: 'nowrap',
          fontWeight: 700,
          border: '2px solid #ffffff',
          borderRadius: '0.25rem',
          padding: '0.5rem 1rem',
          cursor: 'pointer',
        }}
      >
        Exit Support Mode
      </button>
    </div>
    {isWarningOpen ? (
      <AppModal
        id="support-mode-expiry-warning"
        isOpen
        onStateChanged={handleWarningStateChanged}
        title="Support Mode ending soon"
        body={`Support Mode ends in ${remainingLabel}. Save your work now — when the session ends you will be returned to Superuser Tools and any unsaved changes will be lost.`}
        primaryActionText="Keep working"
        onPrimaryAction={dismissWarning}
        secondaryActionText="Exit Support Mode"
        onSecondaryAction={onExit}
      />
    ) : null}
    </>
  );
}
